import { useInfiniteQuery } from "@tanstack/react-query";
import axios from "axios";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Eye,
  User,
  Calendar,
  ArrowRight,
  ArrowLeft,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  Tag,
} from "lucide-react";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa6";
import { buildFilterParams } from "../../../helper/buildFilterParams";
import { ConnectCard1 } from "../../../components/subComponents/ConnectCard1";
import PollsCard from "../../../components/subComponents/PollsCard";
import { toYoutubeEmbed } from "../../../helper/toYoutubeEmbed";
import { saffronRout, saffronURL } from "../../../Url";

const ActivitiesCard = ({ activities, onActivityClick, activeEndpoint }) => {
  // ✅ normalize: if API gives { activities: {...counts} } OR directly {...counts}
  const counts = activities?.activities ?? activities ?? {};

  const activityConfig = [
    { key: "news_count", label: "News", icon: "📰", endpoint: "news" },
    { key: "videos_count", label: "Videos", icon: "🎥", endpoint: "videos" },
    { key: "audio_count", label: "Audios", icon: "🎧", endpoint: "audios" },
    { key: "ebook_count", label: "Ebooks", icon: "📚", endpoint: "ebooks" },

    // ✅ missing option added (your data has webstory_count)
    {
      key: "webstory_count",
      label: "Web Stories",
      icon: "📖",
      endpoint: "webstories",
    },

    // (optional) keep these for future — will auto-hide if count=0
    {
      key: "features_count",
      label: "Features",
      icon: "✨",
      endpoint: "features",
    },
    { key: "poll_count", label: "Polls", icon: "🗳️", endpoint: "polls" },
    { key: "quote_count", label: "Quotes", icon: "💬", endpoint: "quotes" },
    { key: "blogs_count", label: "Blogs", icon: "📝", endpoint: "blogs" },
  ];
  const filtered = useMemo(() => {
    return activityConfig.filter((item) => Number(counts?.[item.key] ?? 0) > 0);
  }, [counts]);

  if (!filtered?.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {filtered.map((item) => {
        const isActive = activeEndpoint === item.endpoint;

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onActivityClick?.(item.endpoint)}
            className={`
  inline-flex items-center gap-2
  px-3 py-1.5 sm:px-4 sm:py-2
  rounded-full
  text-[12px] sm:text-[13px]
  font-semibold
  transition-all duration-200
  border
  ${
    isActive
      ? `
        bg-gradient-to-r from-orange-500 to-orange-600
        text-white
        border-orange-500
        shadow-md
      `
      : `
        bg-[#fff7f2]
        text-[#9d2e43]
        border-[#9d2e43]/20
        hover:bg-white
        hover:border-orange-300
      `
  }
`}
          >
            <span className="leading-none">{item.label}</span>

            {/* optional count badge (thin) */}
          </button>
        );
      })}
    </div>
  );
};
const LIST_KEY = {
  news: "news",
  videos: "videos",
  audios: "audios",
  ebooks: "ebooks",
  quotes: "quotes",
  polls: "polls",
  webstories: "webstories",
  blogs: "blogs",
  features: "stories", // agar API me key different ho, yaha fix kar dena
};


const ConnectSinglePage = ({ article, activities, onBack, onPrev, onNext }) => {
  const { type } = useParams();
  const location = useLocation();
  const [selectedActivity, setSelectedActivity] = useState(null);

  const isVideo = article?.embed_link || article?.video;
  const iframeUrl = toYoutubeEmbed(isVideo);
  const personId = article?.id;
  const filterParams = useMemo(
    () => buildFilterParams(location.search),
    [location.search],
  );
  const listKey = LIST_KEY[selectedActivity] || selectedActivity;

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        "activityData",
        selectedActivity,
        personId,
        filterParams.toString(),
      ],
      enabled: !!selectedActivity && !!personId,
      initialPageParam: 1,
      queryFn: async ({ pageParam }) => {
        const params = new URLSearchParams(filterParams);
        params.set("people_id", String(personId));
        params.set("page", String(pageParam)); // ✅ pagination param

        const url = `${saffronURL}/api/v1/public/${selectedActivity}?${params.toString()}`;
        const res = await axios.get(url);
        return res.data; // { success, data: { [listKey]:[], pagination:{} }, message }
      },
      getNextPageParam: (lastPage) => {
        const pg = lastPage?.data?.pagination;
        if (!pg) return undefined;
        return pg.current_page < pg.last_page ? pg.current_page + 1 : undefined;
      },
      staleTime: 1000 * 60 * 5,
      retry: false,
    });

  const activity_data = useMemo(() => {
    const pages = data?.pages || [];
    return pages.flatMap((p) => {
      const d = p?.data || {};
      return d?.[listKey] || d?.stories || d?.feature || [];
    });
  }, [data, listKey]);
  const pagination = data?.pages?.[data.pages.length - 1]?.data?.pagination;

  const navigate = useNavigate();

  const handleActivityItemClick = (item, activityType, position = 0) => {
    const slug = item?.slug || item?.post_slug || item?.id;
    if (!slug) return;

    const params = new URLSearchParams(location.search);
    const next = new URLSearchParams();

    ["country", "state", "city", "language", "category"].forEach((k) => {
      const v = params.get(k);
      if (v) next.set(k, v);
    });

    // ✅ these are required for prev/next list building
    next.set("section", "activities");
    next.set("activity", activityType); // e.g. "videos"
    next.set("people_id", String(personId)); // ✅ important

    navigate(
      {
        pathname: `${saffronRout}/details/${activityType}/${slug}`,
        search: `?${next.toString()}`,
      },
      {
        state: {
          sectionKey: "activities",
          activityType, // ✅ list fetch uses this
          peopleId: personId,
          position, // ✅ so currentIndex works
        },
      },
    );

    window.scrollTo(0, 0);
  };
  const onSelectActivity = (endpoint) => {
    setSelectedActivity(endpoint);
    // window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="   rounded-md mt-1">
      <div className="px-4 bg-white py-2 flex flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
        {/* LEFT: Back */}
        <button
          onClick={onBack}
          className="order-1 sm:order-none group inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft
            size={16}
            className="transition-transform group-hover:-translate-x-1"
          />
          Back to list
        </button>

        {/* CENTER: Prev / Next */}
        <div className="order-3 w-full sm:order-none sm:w-auto flex justify-center gap-3">
          {/* PREV */}
          <button
            onClick={onPrev}
            className="group flex items-center gap-2 px-4 py-2 rounded-xl
        border border-gray-200 bg-white
        hover:border-orange-400 hover:bg-orange-50
        disabled:opacity-40"
          >
            <ArrowLeft
              size={16}
              className="transition-transform group-hover:-translate-x-1"
            />
            <span className="text-sm font-semibold">Prev</span>
          </button>

          {/* NEXT */}
          <button
            onClick={onNext}
            className="group flex items-center gap-2 px-4 py-2 rounded-xl
        bg-orange-500 text-white
        hover:bg-orange-600
        disabled:opacity-40"
          >
            <span className="text-sm font-semibold">Next</span>
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </button>
        </div>

        {/* RIGHT: Visit */}
        {article?.post_url && (
          <a
            href={article.post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="
        order-2 sm:order-none
        inline-flex items-center gap-1.5
        px-3 py-1.5 rounded-md
        bg-gray-100 text-gray-700
        font-medium text-xs sm:text-sm
        hover:bg-gray-200 transition-colors
      "
          >
            Visit on Site <span>↗</span>
          </a>
        )}
      </div>

      {/* HERO IMAGE */}
      <div className="w-full overflow-hidden">
        {iframeUrl ? (
          <div className="w-full aspect-video">
            <iframe
              src={iframeUrl}
              title={article?.title}
              className="w-full h-full"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        ) : (
          <img
            src={article?.cover_image || article?.image}
            alt={article?.title}
            className="w-full h-auto max-h-[80vh] object-fill"
          />
        )}
      </div>

      {/* CONTENT */}
      <div className={` mx-auto   relative z-30 bg-[#faedde]`}>
        <article className=" rounded-2xl ">
          {type === "people" && (
            <div className="px-4 py-3">
              <div
                className="
        flex flex-col mx-md:gap-2
        text-[13px] text-gray-700
        divide-y divide-gray-200

        md:flex-row md:items-center md:justify-between
        md:divide-y-0
      "
              >
                {/* LEFT: Info items */}
                <div
                  className="
          flex flex-col
          divide-y divide-gray-200

          sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-1 sm:gap-y-2
          sm:divide-y-0 sm:divide-x sm:divide-gray-200
        "
                >
                  {/* Category */}

                  {/* Location */}
                  <div className="flex items-start gap-2 py-1 sm:py-0 sm:pr-4">
                    <MapPin
                      size={15}
                      className="text-gray-500 flex-shrink-0 mt-[2px]"
                    />
                    <span className="break-words">
                      {[article?.city, article?.state, article?.country_name]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </span>
                  </div>

                  {/* Email */}
                  {article?.email && (
                    <div className="flex items-center gap-2 py-1 sm:py-0 md:px-4">
                      <Mail
                        size={15}
                        className="text-indigo-500 flex-shrink-0"
                      />
                      <a
                        href={`mailto:${article.email}`}
                        className="hover:text-indigo-600 transition break-all sm:truncate sm:max-w-[220px]"
                      >
                        {article.email}
                      </a>
                    </div>
                  )}

                  <div className="flex items-center  sm:divide-y-0 sm:divide-x sm:divide-gray-200">
                    {/* Contact */}
                    {(article?.contact ||
                      article?.phone ||
                      article?.mobile) && (
                      <div className="flex items-center gap-2 py-1 sm:py-0 md:px-4">
                        <Phone
                          size={15}
                          className="text-emerald-500 flex-shrink-0"
                        />
                        <a
                          href={`tel:${(
                            article?.contact ||
                            article?.phone ||
                            article?.mobile
                          )
                            ?.toString()
                            ?.replace(/\s+/g, "")}`}
                          className="hover:text-emerald-600 transition whitespace-nowrap"
                        >
                          {article?.contact ||
                            article?.phone ||
                            article?.mobile}
                        </a>
                      </div>
                    )}

                    {/* WhatsApp */}
                    {article?.whatsapp && (
                      <div className="flex items-center gap-2 py-2 sm:py-0 px-4">
                        <MessageCircle
                          size={15}
                          className="text-green-500 flex-shrink-0"
                        />
                        <a
                          href={`https://wa.me/${article.whatsapp
                            ?.toString()
                            ?.replace(/[^\d]/g, "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:text-green-600 transition whitespace-nowrap"
                        >
                          {article.whatsapp}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT: Social icons */}
                <div className="pt-2 md:pt-0 md:pl-4 md:border-l md:border-gray-200 flex  justify-center md:justify-end">
                  <SocialLinks article={article} />
                </div>
              </div>
            </div>
          )}
          {activities && Object.values(activities).some((val) => val > 0) && (
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b bg-[#faedde]">
              <ActivitiesCard
                activities={activities}
                personId={personId}
                activeEndpoint={selectedActivity}
                onActivityClick={onSelectActivity}
              />
            </div>
          )}
          {selectedActivity ? (
            <>
              <div className="px-4 sm:px-6 lg:px-8 py-2 bg-[#faedde]">
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-700 hover:text-orange-900 transition-colors"
                >
                  <span className="text-base leading-none">←</span>
                  Back to Profile
                </button>
              </div>

              {/* list */}
              <div className="px-5 my-3">
                {isLoading ? (
                  <div className="py-8 text-center text-gray-600">Loading…</div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {selectedActivity === "polls" ? (
                        <PollsCard getPolls={activity_data} />
                      ) : (
                        activity_data?.map((item, idx) => (
                          <ConnectCard1
                            key={item.id}
                            item={item}
                            objimg="object-fill"
                            imgH="h-[190px]"
                            onArticleClick={() =>
                              handleActivityItemClick(
                                item,
                                selectedActivity,
                                idx,
                              )
                            }
                          />
                        ))
                      )}
                    </div>

                    {/* pagination info + View More */}
                    <div className="mt-6 flex items-center justify-center flex-col gap-2">
                      {pagination && (
                        <p className="text-xs text-gray-600">
                          Showing {pagination.to} of {pagination.total}
                        </p>
                      )}

                      {hasNextPage && (
                        <button
                          onClick={() => fetchNextPage()}
                          disabled={isFetchingNextPage}
                          className="        group inline-flex items-center gap-2
        px-6 py-2.5
        rounded-full
        border border-orange-500
        text-orange-600 font-semibold
        transition-all duration-300
        hover:bg-orange-500 hover:text-white
        hover:shadow-lg hover:-translate-y-[1px]"
                        >
                          {isFetchingNextPage ? "Loading…" : "View More"}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="bg-[#faedde] px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center gap-3 sm:gap-4 mb-5">
                {/* LEFT IMAGE */}
                {type === "people" &&
                  (article?.thumb_image ||
                    article?.image ||
                    article?.cover_image) && (
                    <div className="flex-shrink-0">
                      <img
                        src={
                          article?.thumb_image ||
                          article?.image ||
                          article?.cover_image
                        }
                        alt={article?.title || article?.name || "thumbnail"}
                        className="
          w-20 h-20 sm:w-24 sm:h-24
          rounded-full
          object-cover
          border border-gray-200
          shadow-sm
        "
                      />
                    </div>
                  )}

                {/* RIGHT CONTENT */}
                <div className="flex-1 min-w-0">
                  {/* TITLE */}
                  <h1
                    className="
          text-[18px] sm:text-2xl lg:text-3xl
          font-extrabold tracking-tight
          text-gray-900
  
          
        "
                    title={article?.title || article?.name || article?.quote}
                  >
                    {article?.title || article?.name}
                  </h1>

                  {/* META */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {/* AUTHOR */}
                    <span className="flex items-center gap-1.5 md:px-2.5 py-1">
                      <Tag
                        size={15}
                        className="text-orange-500 flex-shrink-0"
                      />
                      <span className="font-semibold text-orange-600 text-[12px]">
                        {article?.itemcategory_name || "Category"}
                      </span>
                    </span>
                    <span
                      className="
            inline-flex items-center gap-1.5
            rounded-full md:px-2.5 py-1
            text-[13px] font-semibold
          text-gray-800
        
          "
                    >
                      <User size={14} className="opacity-70" />
                      <span className="truncate max-w-[160px] sm:max-w-[220px]">
                        {article?.author || "THT"}
                      </span>
                    </span>
                    {/* DATE */}
                    <span
                      className="
            inline-flex items-center gap-1.5
            rounded-full md:px-2.5 py-1
            text-[12px]  font-medium
            text-gray-700
          
          "
                    >
                      <Calendar size={14} className="opacity-70" />
                      {article?.created_at
                        ? new Date(article.created_at).toLocaleDateString(
                            "en-IN",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : "—"}
                    </span>

                    {/* VIEWS */}
                    <span
                      className="
            inline-flex items-center gap-1.5
            rounded-full p-2 md:px-2.5 py-1
            text-[12px] font-semibold
            text-orange-700
          
          "
                    >
                      <Eye size={14} className="opacity-90" />
                      {article?.view ?? 0}
                      <span className="font-medium opacity-80 text-orange-600 ">
                        views
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              {type === "audios" && article?.audio_link && (
                <div className="my-4">
                  <div className="rounded-xl border border-orange-200 bg-white shadow-sm overflow-hidden">
                    <div className="px-4 py-2 bg-orange-50 border-b border-orange-200">
                      <p className="text-[12px] font-semibold text-orange-700">
                        Listen Audio
                      </p>
                    </div>

                    <div className="bg-black/5 px-4 pt-3">
                      <iframe
                        title="audio_link"
                        src={article.audio_link}
                        className="w-full h-[152px] sm:h-[166px]"
                        frameBorder="0"
                        loading="lazy"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      />
                    </div>
                  </div>
                </div>
              )}
              {type === "quotes" ? (
                <div
                  className="description "
                  dangerouslySetInnerHTML={{
                    __html: article?.short_description || article?.quote,
                  }}
                />
              ) : (
                <div
                  className="description "
                  dangerouslySetInnerHTML={{ __html: article?.description }}
                />
              )}
            </div>
          )}

          <div className="px-4 sm:px-6 lg:px-8  py-2 flex bg-white flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
            {/* LEFT: Back */}
            <button
              onClick={onBack}
              className="order-1 sm:order-none group inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft
                size={16}
                className="transition-transform group-hover:-translate-x-1"
              />
              Back to list
            </button>

            {/* CENTER: Prev / Next */}
            <div className="order-3 w-full sm:order-none sm:w-auto flex justify-center gap-3">
              {/* PREV */}
              <button
                onClick={onPrev}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
          border border-gray-200 bg-white
          hover:border-orange-400 hover:bg-orange-50
          disabled:opacity-40"
              >
                <ArrowLeft
                  size={16}
                  className="transition-transform group-hover:-translate-x-1"
                />
                <span className="text-sm font-semibold">Prev</span>
              </button>

              {/* NEXT */}
              <button
                onClick={onNext}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
          bg-orange-500 text-white
          hover:bg-orange-600
          disabled:opacity-40"
              >
                <span className="text-sm font-semibold">Next</span>
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>

            {/* RIGHT: Visit */}
            {article?.post_url && (
              <a
                href={article.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="
          order-2 sm:order-none
          inline-flex items-center gap-1.5
          px-3 py-1.5 rounded-md
          bg-gray-100 text-gray-700
          font-medium text-xs sm:text-sm
          hover:bg-gray-200 transition-colors
        "
              >
                Visit on Site <span>↗</span>
              </a>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};

const SocialLinks = ({ article }) => {
  const socials = [
    {
      key: "facebook_url",
      label: "Facebook",
      icon: FaFacebook,
      href: article?.facebook_url,
    },
    {
      key: "twitter_url",
      label: "Twitter",
      icon: FaTwitter,
      href: article?.twitter_url,
    },
    {
      key: "linkedin_url",
      label: "LinkedIn",
      icon: FaLinkedin,
      href: article?.linkedin_url,
    },
    {
      key: "instagram_url",
      label: "Instagram",
      icon: FaInstagram,
      href: article?.instagram_url,
    },
    {
      key: "youtube_url",
      label: "YouTube",
      icon: FaYoutube,
      href: article?.youtube_url,
    },
  ].filter((s) => s.href && s.href.trim() !== "");

  if (!socials?.length) return null;

  return (
    <div className="flex items-center gap-2">
      {/* <Share2 size={14} className="opacity-70 text-gray-500" /> */}
      <div className="flex items-center gap-2">
        {socials.map(({ key, label, icon: Icon, href }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={label}
            title={label}
            className="
              inline-flex items-center justify-center
              h-9 w-9 rounded-full
              border border-gray-200 bg-white
              text-gray-700
              hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700
              transition
              leading-none
            "
          >
            <Icon className="text-[16px] leading-none" />
          </a>
        ))}
      </div>
    </div>
  );
};

export default ConnectSinglePage;
